// KnowledgeBank.RelationName — branded relation vocabulary.
//
// Per `.docs/v1/design/bounded-contexts.md` §9 and Q4 of
// `open-questions.md` §18 (Slice 6):
//
//   `RelationName = NonEmpty<string> & { __brand: "RelationName" }`
//
// Branded, not a closed union. TS won't catch relation-name typos —
// only that they're `NonEmpty` at the parse boundary. The trade-off
// is deliberate: the domain opens for Author-defined relations
// post-MVP without reshape. Aligns with `TagValue` and
// `DescriptionText` brand discipline.
//
// The seeded MVP `const`s live below via `mk`; each downstream
// PM / query references them by name. Counting the seeded set is
// deliberately avoided (per [[feedback_docs_as_invariants]]) —
// grow the list without churning the header.
export { annotates, answersTo, broader, citesElement, contradicts, covers, definedIn, derivesFrom, drawsOn, explains, hasActor, hasDescription, hasQualityScore, hasTag, mk, oaEnd, oaExact, oaHasSelector, oaHasSource, oaPrefix, oaStart, oaSuffix, practises, prerequisiteOf, provGeneratedAtTime, provWasAttributedTo, provWasDerivedFrom, rdfValue, related, schemaAbout, schemaAudience, schemaText, serves, supports, teaches, touches, };
// Trusting `mk`; the parse-boundary guard (`RelationNameEmpty` in
// `error.ts`) rejects empty strings before a `decide`-time value
// lands. Same shape as every other `NonEmpty` brand in core.
const mk = (s) => s;
// === Seeded consts ===
//
// The starting set from `knowledge-bank.md` §8 plus `touches` (the
// Objective-coverage axis of the structural-quality check). The
// `has-content-hash` / `has-provenance` reification targets were
// removed in P6 step C — they were write-only literal-object triples
// with no readers, and that data already lives on the owning Resource.
// Post-MVP vocabulary growth is Author-driven via `DefineRelation`;
// adding a const here is the exception, not the rule.
const teaches = mk("teaches");
const covers = mk("covers");
const touches = mk("touches");
const prerequisiteOf = mk("prerequisite-of");
const derivesFrom = mk("derives-from");
// `serves` — whole-material → the LearningContext it is authored for.
// Twin of `derives-from` (whole-material → whole-source): the goal-anchor
// edge the CEGIS score reads to pick the LearningContext its tiers score
// against (Fas 4 Step 2b, DESIGN-fas4-2b-goal-anchored-score.md §1). The
// object is a `learning_context_ref`, already a valid KB `EntityRef`, so
// no new ref shape is owed. Reification into a mutable triple is Step 3.
const serves = mk("serves");
const hasDescription = mk("has-description");
const hasTag = mk("has-tag");
const hasQualityScore = mk("has-quality-score");
const contradicts = mk("contradicts");
const supports = mk("supports");
const annotates = mk("annotates");
const hasActor = mk("has-actor");
// Goal coverage (Kontext goal, 2026-10-06). How a chat's goal (a
// LearningContext, its Objectives and Terms) is tied to the documents it
// answers to and to the material parts that meet it:
//
//   (LearningContext, answers-to, Source whole ref)  — the requirement
//       document (an ämnesplan, a kursplan, a competence framework);
//   (Objective, answers-to, Source part ref)          — the requirement's
//       own words for that objective (locator = a located quote);
//   (Term, defined-in, Source part ref)               — the source's own
//       definition of the term (locator = a located quote);
//   (Material part ref, explains, Objective | Term)   — this part explains
//       the objective / defines the term;
//   (Material part ref, practises, Objective)         — this question lets
//       the learner practise the objective.
//
// The material side is a `part_resource_ref` whose locator is the element
// id. The agent writes them (tools), the
// coverage fold reads them; a link to a part or entity that no longer
// resolves is dropped at read time, never shown as coverage.
const answersTo = mk("answers-to");
const definedIn = mk("defined-in");
const explains = mk("explains");
const practises = mk("practises");
// RETIRED (knowledge-bank.md §11, slice G): `cites-element`, material
// element → source paragraph. Nothing writes or reads it any more; a
// material statement is backed by a knowledge-bank claim
// (`provWasDerivedFrom`). Kept in the vocabulary only so old logs that
// asserted it still fold.
const citesElement = mk("cites-element");
// The goal as a projection (D56, .claude/analysis/top-down-authoring-2026-10-07/
// design.md). A goal (LearningContext) references more workspace entities:
//
//   (LearningContext, broader, LearningContext) — a sub-goal points at its
//       parent goal (SKOS-style: `broader` lives on the narrower side;
//       `narrower` is never stored, it is `broader` read backwards). A tree:
//       at most one `broader` per subject and no cycles, refused at decide
//       time (`decide.ts`). The same relation carries sub-concepts (D55
//       S10): (narrower Concept, broader, broader Concept), same tree rule:
//       no polyhierarchy; a concept's second home is `related`.
//   (LearningContext, draws-on, Source whole ref) — a source the goal is built
//       from (not the requirement it answers to): it follows the goal into
//       every chat that works toward it.
const broader = mk("broader");
const drawsOn = mk("draws-on");
// (Concept, related, Concept) — two concepts that belong together without
// one being under the other (SKOS `related`; D55 S10). Symmetric: stored
// once, subject = the concept it was linked from, read in both directions.
// Asserting the reverse of a stored pair is a no-op (the same fact), and a
// concept is never related to itself (`decide.ts`).
const related = mk("related");
// Claims (knowledge-bank.md §11.2). Standard RDF predicates, named by CURIE
// so their ids follow the existing scheme (`rel:prov:wasDerivedFrom`); an RDF
// export expands each prefix to its IRI (`rdf.ts`).
const schemaText = mk("schema:text");
const schemaAbout = mk("schema:about");
const schemaAudience = mk("schema:audience");
const provWasAttributedTo = mk("prov:wasAttributedTo");
const provGeneratedAtTime = mk("prov:generatedAtTime");
const provWasDerivedFrom = mk("prov:wasDerivedFrom");
const oaHasSource = mk("oa:hasSource");
const oaHasSelector = mk("oa:hasSelector");
const oaExact = mk("oa:exact");
const oaPrefix = mk("oa:prefix");
const oaSuffix = mk("oa:suffix");
const oaStart = mk("oa:start");
const oaEnd = mk("oa:end");
const rdfValue = mk("rdf:value");
//# sourceMappingURL=relationName.js.map